// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import { useEvolveGet, type MachineElement, type ElementId } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useUpdateGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { BendQAModalContents } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendQAModalContents';

type Props = ModalRegistryManagedProps & {
  bendId: ElementId;
  /** Requests that the bend be marked as done. The provider owns the action and closes the modal when it succeeds. */
  onMarkBendDone: (bend: MachineElement) => Promise<void>;
  onBendUpdated?: (updatedBend: MachineElement) => void;
};

/**
 * @remarks Registered in the machine element modal registry and opened by {@link BendQAProvider} whenever a `bendId`
 * is present in the URL. Call `viewBend` from {@link useBendQA} instead of rendering or opening this directly.
 */
export const BendQAModal = ({ bendId, onMarkBendDone, onBendUpdated, onClose, ...props }: Props) => {
  const { ensureProjectVisible } = useUpdateGlobalFilters();
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');
  const { loading, apiCall: getBendData } = useEvolveGet(`shop/fabrication/element/:elementId`);
  const [bendData, setBendData] = useState<MachineElement | null>(null);

  // Reload the bend data whenever `bendId` changes
  useEffect(() => {
    let cancelled = false;
    void getBendData({
      url: `shop/fabrication/element/${bendId}`,
    })
      .then(async (e) => {
        if (cancelled) return;
        if (isNil(e.benderProfile)) {
          notifications.show({
            title: `No machine profile selected for ${e.name}`,
            message: isNotNil(e.workOrderName)
              ? `Ensure the facility associated with work order '${e.workOrderName}' has a default machine profile for conduit size ${e.materialSize}.`
              : `Ensure this bend is on a work order with a default machine profile for its conduit size, ${e.materialSize}.`,
            color: 'red',
          });
          onClose();
          return;
        }
        setBendData(e);
      })
      .catch((e) => {
        if (cancelled) return;
        notifications.show({ title: 'Failed to load bend data', message: String(e), color: 'red' });
        onClose();
      });
    return () => {
      cancelled = true;
    };
  }, [bendId, getBendData, onClose]);

  useEffect(() => {
    if (isNotNil(bendData?.projectId)) {
      // The user can directly navigate to a bend using a route parameter, so make sure the project is visible
      void ensureProjectVisible(bendData.projectId);
    }
  }, [bendData?.projectId, ensureProjectVisible]);

  return (
    <WrappedModal
      title={bendData?.name ?? ''}
      loading={loading}
      // Users without write access shouldn't be able to mark bends done/unfinished.
      // They can still view the modal and close it with the "X" button though.
      hideModalButtons={!hasFabricationFullAccess}
      confirmationText="Mark as done"
      confirmationTooltip={
        !bendData?.canMarkBendDone ? 'This bend must be on a Released work order to mark as done.' : undefined
      }
      disabled={!bendData?.canMarkBendDone}
      // Marking the bend as done may need to be confirmed first, in which case this modal has to stay open.
      closeOnConfirm={false}
      onConfirm={async () => {
        if (isNil(bendData)) return;
        await onMarkBendDone(bendData);
      }}
      bodyProps={{ p: 0, display: 'flex', flex: 1, style: { flexDirection: 'column' } }}
      onClose={onClose}
      fullScreen
      {...props}
    >
      <>
        {isNotNil(bendData) &&
          (bendData.hasBendData ? (
            <BendQAModalContents
              element={bendData}
              onBendUpdated={(updated: MachineElement) => {
                setBendData(updated);
                onBendUpdated?.(updated);
              }}
            />
          ) : (
            <Text>No bend data available for this element.</Text>
          ))}
      </>
    </WrappedModal>
  );
};
